import divingCatchTuningData from '../../../data/diving-catch-tuning.json';
import { playHash } from './helpers.js';

type HeroMomentTuning = {
  minimumAirYards: number;
  completionChance: number;
  attemptChance: number;
  interceptionChance: number;
  leadInFrames: number;
  followThroughFrames: number;
  receptionForwardDiveYards: number;
  presentationLiftPixels: number;
  slowMotionRate: number;
  zoomScale: number;
  soundVolume: number;
};

export type DivingHeroMomentPlan = {
  result: 'completion' | 'attempt' | 'interception';
  role: string;
  rosterId: string | null;
  teamType: 'offense' | 'defense';
  airYards: number;
  startSimFrame: number;
  contactSimFrame: number;
  endSimFrame: number;
  zoomScale: number;
  soundVolume: number;
};

export type DivingHeroPlaybackTiming = {
  plan: DivingHeroMomentPlan;
  baseStartMs: number;
  baseEndMs: number;
  presentedEndMs: number;
  extraMs: number;
  slowMotionRate: number;
};

const HERO_TUNING = (divingCatchTuningData as { heroMoment: HeroMomentTuning }).heroMoment;
const planCache = new WeakMap<object, DivingHeroMomentPlan | null>();

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function deepBallDistance(play: any): number {
  const replay = play?.arcadeReplay;
  const startYardLine = Number(replay?.startLos?.yardLine ?? play?.startYardLine ?? 0);
  const deepestBallYardLine = Math.max(
    startYardLine,
    ...(replay?.ballPath || [])
      .map((point: any) => Number(point?.yardLine))
      .filter(Number.isFinite),
  );
  return Math.max(
    0,
    Number(replay?.metadata?.airYards || play?.airYards || 0),
    deepestBallYardLine - startYardLine,
  );
}

function resultForDiveEvent(event: any, play: any): DivingHeroMomentPlan['result'] {
  if (event?.type === 'diving_catch_attempt') {
    return 'attempt';
  }
  const recordedResult = String(
    event?.payload?.result
      || play?.arcadeReplay?.metadata?.catchResult
      || '',
  ).toLowerCase();
  return recordedResult === 'interception' ? 'interception' : 'completion';
}

function chanceForResult(result: DivingHeroMomentPlan['result']): number {
  if (result === 'interception') return HERO_TUNING.interceptionChance;
  if (result === 'attempt') return HERO_TUNING.attemptChance;
  return HERO_TUNING.completionChance;
}

function qualifyingDiveFrames(play: any, rosterId: string | null, role: string) {
  return (play?.arcadeReplay?.frames || []).filter((frame: any) => (
    frame.players?.some?.((player: any) => {
      const assignment = String(player?.assignmentKind || '');
      const samePlayer = rosterId
        ? String(player?.rosterId || '') === rosterId
        : String(player?.rendererRole || player?.roleKey || '') === role;
      return samePlayer && (
        assignment === 'catch-dive-launch'
        || assignment === 'catch-dive-extension'
        || assignment === 'catch-dive-contact'
        || assignment === 'catch-dive-landing'
      );
    })
  ));
}

export function resolveDivingHeroMoment(
  play: any,
  options: { forcePresentation?: boolean } = {},
): DivingHeroMomentPlan | null {
  if (!play || typeof play !== 'object') {
    return null;
  }
  if (!options.forcePresentation && planCache.has(play)) {
    return planCache.get(play) ?? null;
  }
  const replay = play.arcadeReplay;
  if (play.family !== 'pass' || replay?.source !== 'arcade-sim-v1' || !Array.isArray(replay.frames)) {
    if (!options.forcePresentation) planCache.set(play, null);
    return null;
  }
  const airYards = deepBallDistance(play);
  if (airYards < HERO_TUNING.minimumAirYards) {
    if (!options.forcePresentation) planCache.set(play, null);
    return null;
  }

  const event = replay.events?.find?.((candidate: any) => (
    candidate?.type === 'diving_catch' || candidate?.type === 'diving_catch_attempt'
  ));
  if (!event) {
    if (!options.forcePresentation) planCache.set(play, null);
    return null;
  }
  const result = resultForDiveEvent(event, play);
  const chance = chanceForResult(result);
  const chanceRoll = (playHash(play, `diving-hero:${result}:${event.simFrame}`) % 1000) / 1000;
  if (!options.forcePresentation && chanceRoll >= chance) {
    planCache.set(play, null);
    return null;
  }

  const rosterId = event.payload?.targetId ? String(event.payload.targetId) : null;
  const role = String(
    event.payload?.targetRole
      || (result === 'interception' ? replay.participants?.interceptorRole : replay.participants?.targetRole)
      || '',
  );
  const frames = qualifyingDiveFrames(play, rosterId, role);
  if (!frames.length || !role) {
    if (!options.forcePresentation) planCache.set(play, null);
    return null;
  }
  const contactFrame = frames.find((frame: any) => frame.players.some((player: any) => (
    (rosterId ? String(player?.rosterId || '') === rosterId : String(player?.rendererRole || '') === role)
    && player.assignmentKind === 'catch-dive-contact'
  ))) || frames[Math.floor(frames.length * 0.62)] || frames.at(-1);
  const firstFrame = frames[0];
  const lastFrame = frames.at(-1);
  const firstPlayer = firstFrame.players.find((player: any) => (
    rosterId ? String(player?.rosterId || '') === rosterId : String(player?.rendererRole || '') === role
  ));
  const plan: DivingHeroMomentPlan = {
    result,
    role,
    rosterId,
    teamType: firstPlayer?.side === 'defense' || result === 'interception' ? 'defense' : 'offense',
    airYards,
    startSimFrame: Math.max(0, Number(firstFrame.simFrame || 0) - HERO_TUNING.leadInFrames),
    contactSimFrame: Number(contactFrame?.simFrame ?? event.simFrame ?? firstFrame.simFrame),
    endSimFrame: Number(lastFrame?.simFrame ?? event.simFrame ?? firstFrame.simFrame) + HERO_TUNING.followThroughFrames,
    zoomScale: HERO_TUNING.zoomScale,
    soundVolume: HERO_TUNING.soundVolume,
  };
  if (!options.forcePresentation) planCache.set(play, plan);
  return plan;
}

export function divingHeroMomentAtFrame(
  plan: DivingHeroMomentPlan | null,
  simFrame: number,
) {
  if (!plan || simFrame < plan.startSimFrame || simFrame > plan.endSimFrame) {
    return null;
  }
  const approachSpan = Math.max(1, plan.contactSimFrame - plan.startSimFrame);
  const releaseSpan = Math.max(1, plan.endSimFrame - plan.contactSimFrame);
  const intensity = simFrame <= plan.contactSimFrame
    ? clamp((simFrame - plan.startSimFrame) / approachSpan, 0, 1)
    : clamp(1 - (simFrame - plan.contactSimFrame) / releaseSpan, 0, 1);
  return {
    ...plan,
    intensity,
    phase: simFrame < plan.contactSimFrame ? 'approach' : simFrame === plan.contactSimFrame ? 'contact' : 'release',
  };
}

export function divingHeroPresentationOffset(
  plan: DivingHeroMomentPlan | null,
  simFrame: number,
): { forwardYards: number; liftPixels: number } | null {
  if (!plan || simFrame < plan.startSimFrame || simFrame > plan.endSimFrame) return null;
  const launchSpan = Math.max(1, plan.contactSimFrame - plan.startSimFrame);
  const launchProgress = clamp((simFrame - plan.startSimFrame) / launchSpan, 0, 1);
  const easedForwardProgress = launchProgress * launchProgress * (3 - 2 * launchProgress);
  const fullSpan = Math.max(1, plan.endSimFrame - plan.startSimFrame);
  const fullProgress = clamp((simFrame - plan.startSimFrame) / fullSpan, 0, 1);
  return {
    forwardYards: HERO_TUNING.receptionForwardDiveYards * easedForwardProgress,
    liftPixels: Math.sin(fullProgress * Math.PI) * HERO_TUNING.presentationLiftPixels,
  };
}

export function divingHeroPlaybackTiming(
  play: any,
  baseDurationMs: number,
  liveDurationMs: number,
  options: { forcePresentation?: boolean } = {},
): DivingHeroPlaybackTiming | null {
  const plan = resolveDivingHeroMoment(play, options);
  const frames = play?.arcadeReplay?.frames || [];
  if (!plan || frames.length < 2 || liveDurationMs <= 0) {
    return null;
  }
  const lastIndex = frames.length - 1;
  const frameIndexAtOrAfter = (simFrame: number) => {
    const index = frames.findIndex((frame: any) => Number(frame.simFrame) >= simFrame);
    return index < 0 ? lastIndex : index;
  };
  const startIndex = frameIndexAtOrAfter(plan.startSimFrame);
  const endIndex = Math.max(startIndex + 1, frameIndexAtOrAfter(plan.endSimFrame));
  const liveStartMs = Math.max(0, baseDurationMs - liveDurationMs);
  const baseStartMs = liveStartMs + (startIndex / lastIndex) * liveDurationMs;
  const baseEndMs = Math.min(
    baseDurationMs,
    liveStartMs + (Math.min(lastIndex, endIndex) / lastIndex) * liveDurationMs,
  );
  const slowMotionRate = clamp(HERO_TUNING.slowMotionRate, 0.2, 0.9);
  const baseWindowMs = Math.max(1, baseEndMs - baseStartMs);
  const presentedWindowMs = baseWindowMs / slowMotionRate;
  return {
    plan,
    baseStartMs,
    baseEndMs,
    presentedEndMs: baseStartMs + presentedWindowMs,
    extraMs: presentedWindowMs - baseWindowMs,
    slowMotionRate,
  };
}

export function divingHeroPresentedDurationMs(
  play: any,
  baseDurationMs: number,
  liveDurationMs: number,
  options: { forcePresentation?: boolean } = {},
): number {
  const timing = divingHeroPlaybackTiming(play, baseDurationMs, liveDurationMs, options);
  return Math.round(baseDurationMs + (timing?.extraMs || 0));
}

export function divingHeroBaseElapsedMs(
  play: any,
  presentedElapsedMs: number,
  baseDurationMs: number,
  liveDurationMs: number,
  options: { forcePresentation?: boolean } = {},
): number {
  const timing = divingHeroPlaybackTiming(play, baseDurationMs, liveDurationMs, options);
  if (!timing || presentedElapsedMs <= timing.baseStartMs) {
    return clamp(presentedElapsedMs, 0, baseDurationMs);
  }
  // The public duration is rounded to milliseconds. Do not leave the replay
  // a fraction of a frame short of its final pose after the slow-motion window.
  if (presentedElapsedMs >= Math.round(baseDurationMs + timing.extraMs)) return baseDurationMs;
  if (presentedElapsedMs <= timing.presentedEndMs) {
    return clamp(
      timing.baseStartMs + (presentedElapsedMs - timing.baseStartMs) * timing.slowMotionRate,
      0,
      baseDurationMs,
    );
  }
  return clamp(
    timing.baseEndMs + (presentedElapsedMs - timing.presentedEndMs),
    0,
    baseDurationMs,
  );
}

export function divingHeroMomentTuningForTests(): Readonly<HeroMomentTuning> {
  return HERO_TUNING;
}
